import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Users,
  UserPlus,
  MoreVertical,
  Mail,
  Phone,
  Shield,
  Clock,
  Trash2,
  Edit2,
  Send,
  Crown,
  Briefcase,
  HardHat,
  User,
  ArrowLeft
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const ROLES = {
  owner: { label: "Owner", color: "bg-amber-100 text-amber-700", icon: Crown },
  project_manager: { label: "Project Manager", color: "bg-purple-100 text-purple-700", icon: Briefcase },
  employee: { label: "Employee", color: "bg-blue-100 text-blue-700", icon: User },
  subcontractor: { label: "Subcontractor", color: "bg-green-100 text-green-700", icon: HardHat },
};

const getRoleInfo = (role) => ROLES[role] || { label: role, color: "bg-slate-100 text-slate-700", icon: User };

const formatJoinedDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
};

export default function TeamPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [inviteData, setInviteData] = useState({
    email: "",
    role: "employee",
    name: ""
  });
  const [sending, setSending] = useState(false);
  const [resendingInviteId, setResendingInviteId] = useState(null);

  const isOwner = user?.role === "owner";
  const isManager = user?.role === "owner" || user?.role === "project_manager" || user?.role === "office_admin";

  useEffect(() => {
    fetchTeamData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTeamData = async () => {
    try {
      const [teamRes, invitesRes] = await Promise.all([
        axios.get(`${API}/team`),
        isManager ? axios.get(`${API}/invitations`) : Promise.resolve({ data: [] })
      ]);
      setTeamMembers(teamRes.data);
      setInvitations(invitesRes.data);
    } catch (error) {
      toast.error("Failed to load team data");
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/invitations`, inviteData);
      toast.success(`Invitation sent to ${inviteData.email}`);
      setShowInviteModal(false);
      setInviteData({ email: "", role: "employee", name: "" });
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setSending(false);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await axios.delete(`${API}/invitations/${inviteId}`);
      toast.success("Invitation cancelled");
      fetchTeamData();
    } catch (error) {
      toast.error("Failed to cancel invitation");
    }
  };

  const handleResendInvite = async (inviteId) => {
    setResendingInviteId(inviteId);
    try {
      await axios.post(`${API}/invitations/${inviteId}/resend`);
      toast.success("Invitation resent with a fresh secure link");
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend invitation");
    } finally {
      setResendingInviteId(null);
    }
  };

  const handleUpdateMember = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/team/${selectedMember.user_id}`, {
        name: selectedMember.name,
        phone: selectedMember.phone,
        role: selectedMember.role
      });
      toast.success("Team member updated");
      setShowEditModal(false);
      setSelectedMember(null);
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update member");
    }
  };

  const handleRemoveMember = async (userId, name) => {
    if (!window.confirm(`Are you sure you want to remove ${name} from the team?`)) return;
    try {
      await axios.delete(`${API}/team/${userId}`);
      toast.success("Team member removed");
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove member");
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Group members by role
  const membersByRole = {
    owner: teamMembers.filter(m => m.role === "owner"),
    project_manager: teamMembers.filter(m => m.role === "project_manager"),
    employee: teamMembers.filter(m => m.role === "employee"),
    subcontractor: teamMembers.filter(m => m.role === "subcontractor"),
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="team-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Team Management
          </h1>
          <p className="text-slate-600 mt-1">
            {teamMembers.length} team members
          </p>
        </div>
        {isManager && (
          <Button onClick={() => setShowInviteModal(true)} data-testid="invite-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Team Member
          </Button>
        )}
      </div>

      {/* Team Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(ROLES).map(([roleKey, roleInfo]) => {
          const count = membersByRole[roleKey]?.length || 0;
          const Icon = roleInfo.icon;
          return (
            <Card key={roleKey} className="stat-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">{roleInfo.label}s</p>
                    <p className="text-2xl font-bold text-slate-900">{count}</p>
                  </div>
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${roleInfo.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Pending Invitations */}
      {isManager && invitations.length > 0 && (
        <Card data-testid="pending-invites">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-500" />
              Pending Invitations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invitations.map((invite) => {
                const roleInfo = getRoleInfo(invite.role);
                return (
                  <div 
                    key={invite.invite_id} 
                    className="flex flex-wrap items-center justify-between gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-100"
                    data-testid={`pending-invite-${invite.invite_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-yellow-600" />
                      <div>
                        <p className="font-medium text-slate-900">{invite.email}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <Badge className={roleInfo.color}>{roleInfo.label}</Badge>
                          <span className="text-xs capitalize text-slate-500" data-testid={`invite-delivery-status-${invite.invite_id}`}>
                            Email: {(invite.delivery_status || "pending").replaceAll("_", " ")}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResendInvite(invite.invite_id)}
                        disabled={resendingInviteId === invite.invite_id}
                        data-testid={`resend-invite-${invite.invite_id}`}
                      >
                        <Send className="mr-1 h-4 w-4" />
                        {resendingInviteId === invite.invite_id ? "Sending…" : "Resend"}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleCancelInvite(invite.invite_id)}
                        className="text-red-600 hover:text-red-700"
                        aria-label={`Cancel invitation for ${invite.email}`}
                        data-testid={`cancel-invite-${invite.invite_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team Members Table */}
      <Card data-testid="team-members">
        <CardHeader>
          <CardTitle className="text-lg">All Team Members</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Joined</TableHead>
                {isManager && <TableHead className="w-12"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {teamMembers.map((member) => {
                const roleInfo = getRoleInfo(member.role);
                const Icon = roleInfo.icon;
                const isCurrentUser = member.user_id === user?.user_id;
                
                return (
                  <TableRow key={member.user_id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={member.picture} />
                          <AvatarFallback className={roleInfo.color}>
                            {getInitials(member.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="font-medium text-slate-900">
                            {member.name}
                            {isCurrentUser && <span className="text-slate-400 ml-2">(You)</span>}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={roleInfo.color}>
                        <Icon className="w-3 h-3 mr-1" />
                        {roleInfo.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-600">{member.email}</TableCell>
                    <TableCell className="text-slate-600">{member.phone || "-"}</TableCell>
                    <TableCell className="text-slate-500">
                      {formatJoinedDate(member.created_at)}
                    </TableCell>
                    {isManager && (
                      <TableCell>
                        {!isCurrentUser && member.role !== "owner" && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`Manage ${member.name}`} data-testid={`team-member-actions-desktop-${member.user_id}`}>
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => {
                                setSelectedMember(member);
                                setShowEditModal(true);
                              }}>
                                <Edit2 className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              {isOwner && (
                                <DropdownMenuItem 
                                  onClick={() => handleRemoveMember(member.user_id, member.name)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Remove
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>

          <div className="divide-y divide-slate-100 md:hidden" data-testid="team-members-mobile-list">
            {teamMembers.map((member) => {
              const roleInfo = getRoleInfo(member.role);
              const Icon = roleInfo.icon;
              const isCurrentUser = member.user_id === user?.user_id;
              return (
                <article key={member.user_id} className="space-y-3 py-4 first:pt-0 last:pb-0" data-testid={`team-member-mobile-${member.user_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <Avatar className="h-9 w-9 shrink-0">
                        <AvatarImage src={member.picture} />
                        <AvatarFallback className={roleInfo.color}>{getInitials(member.name)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="truncate font-medium text-slate-900" data-testid={`team-member-name-mobile-${member.user_id}`}>
                          {member.name}{isCurrentUser && <span className="ml-1 text-slate-400">(You)</span>}
                        </p>
                        <p className="truncate text-sm text-slate-500">{member.email}</p>
                      </div>
                    </div>
                    {isManager && !isCurrentUser && member.role !== "owner" && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" aria-label={`Manage ${member.name}`} data-testid={`team-member-actions-mobile-${member.user_id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setSelectedMember(member); setShowEditModal(true); }} data-testid={`edit-team-member-mobile-${member.user_id}`}>
                            <Edit2 className="mr-2 h-4 w-4" />Edit
                          </DropdownMenuItem>
                          {isOwner && (
                            <DropdownMenuItem onClick={() => handleRemoveMember(member.user_id, member.name)} className="text-red-600" data-testid={`remove-team-member-mobile-${member.user_id}`}>
                              <Trash2 className="mr-2 h-4 w-4" />Remove
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={roleInfo.color}><Icon className="mr-1 h-3 w-3" />{roleInfo.label}</Badge>
                    {member.phone && <span className="text-xs text-slate-500">{member.phone}</span>}
                    <span className="text-xs text-slate-400">Joined {formatJoinedDate(member.created_at)}</span>
                  </div>
                </article>
              );
            })}
          </div>

          {teamMembers.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No team members yet</p>
              <p className="text-sm">Invite your first team member to get started</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Role Permissions Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Role Permissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="w-5 h-5 text-amber-600" />
                <h4 className="font-semibold text-amber-800">Owner</h4>
              </div>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• Full access to everything</li>
                <li>• Manage all team members</li>
                <li>• View all projects & leads</li>
                <li>• Billing & settings</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-purple-50 border border-purple-100">
              <div className="flex items-center gap-2 mb-2">
                <Briefcase className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold text-purple-800">Project Manager</h4>
              </div>
              <ul className="text-sm text-purple-700 space-y-1">
                <li>• Manage projects & schedules</li>
                <li>• View & manage leads</li>
                <li>• Invite employees & subs</li>
                <li>• View all team activities</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-blue-800">Employee</h4>
              </div>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• View assigned schedules</li>
                <li>• Clock in/out (timecards)</li>
                <li>• Add notes & photos</li>
                <li>• Complete assigned tasks</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-green-50 border border-green-100">
              <div className="flex items-center gap-2 mb-2">
                <HardHat className="w-5 h-5 text-green-600" />
                <h4 className="font-semibold text-green-800">Subcontractor</h4>
              </div>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• View assigned jobs only</li>
                <li>• Add notes & photos</li>
                <li>• Complete assigned tasks</li>
                <li>• No timecard required</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation email to add someone to your team.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="form-field">
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={inviteData.email}
                onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                placeholder="colleague@example.com"
                required
                data-testid="invite-email"
              />
            </div>
            <div className="form-field">
              <Label>Role *</Label>
              <Select
                value={inviteData.role}
                onValueChange={(v) => setInviteData({ ...inviteData, role: v })}
              >
                <SelectTrigger data-testid="invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isOwner && (
                    <SelectItem value="project_manager">
                      <span className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        Project Manager
                      </span>
                    </SelectItem>
                  )}
                  {isOwner && (
                    <SelectItem value="office_admin">
                      <span className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        Office Administrator
                      </span>
                    </SelectItem>
                  )}
                  <SelectItem value="employee">
                    <span className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Employee
                    </span>
                  </SelectItem>
                  <SelectItem value="subcontractor">
                    <span className="flex items-center gap-2">
                      <HardHat className="w-4 h-4" />
                      Subcontractor
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowInviteModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={sending} data-testid="send-invite-btn">
                {sending ? (
                  <>Sending...</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Invitation
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Member Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <form onSubmit={handleUpdateMember} className="space-y-4">
              <div className="form-field">
                <Label>Name</Label>
                <Input
                  value={selectedMember.name}
                  onChange={(e) => setSelectedMember({ ...selectedMember, name: e.target.value })}
                  data-testid="edit-name"
                />
              </div>
              <div className="form-field">
                <Label>Phone</Label>
                <Input
                  value={selectedMember.phone || ""}
                  onChange={(e) => setSelectedMember({ ...selectedMember, phone: e.target.value })}
                  placeholder="+1 234 567 8900"
                  data-testid="edit-phone"
                />
              </div>
              {isOwner && selectedMember.role !== "owner" && (
                <div className="form-field">
                  <Label>Role</Label>
                  <Select
                    value={selectedMember.role}
                    onValueChange={(v) => setSelectedMember({ ...selectedMember, role: v })}
                  >
                    <SelectTrigger data-testid="edit-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="project_manager">Project Manager</SelectItem>
                      <SelectItem value="office_admin">Office Administrator</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="subcontractor">Subcontractor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowEditModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-member-btn">
                  Save Changes
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
